import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Button,
  Tag,
  Space,
  Avatar,
  Tooltip,
  message,
  Modal,
  Drawer,
  Steps,
  Typography,
  Descriptions,
  Badge,
  Form,
  InputNumber,
  Switch,
  Divider,
  List,
  Popconfirm,
  ColorPicker,
  Radio,
  Spin,
  Checkbox,
  Alert
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  CrownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  StarOutlined,
  SettingOutlined,
  UserOutlined,
  DatabaseOutlined,
  ApiOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { subscriptionPlansService } from '../services/subscriptionPlansService';
import SubscriptionPlanDetail from './SubscriptionPlanDetail';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { confirm } = Modal;
const { TextArea } = Input;

// Available features for subscription plans. `mandatory: true` features are
// always selected (even on Free tier) and can't be unchecked — these are the
// core CRM modules that every tenant needs to use the product at all.
const AVAILABLE_FEATURES = [
  { id: 'lead_management', name: 'Lead Management', description: 'Comprehensive lead tracking and management', mandatory: true },
  { id: 'deal_management', name: 'Deal Management', description: 'Complete deal pipeline and tracking', mandatory: true },
  { id: 'meetings', name: 'Meetings', description: 'Schedule and manage meetings', mandatory: true },
  { id: 'task_management', name: 'Task Management', description: 'Task creation and tracking', mandatory: true },
  { id: 'analytics', name: 'Analytics', description: 'Advanced analytics and insights' },
  { id: 'proposal_development', name: 'Proposal Development', description: 'Create and manage proposals' },
  { id: 'whatsapp_integration', name: 'WhatsApp Integration', description: 'Integrated WhatsApp communication' },
  { id: 'ai_calling', name: 'AI Calling', description: 'AI-powered calling features' },
  { id: 'automation_engine', name: 'Automation Engine', description: 'Workflow automation capabilities' },
  { id: 'reports', name: 'Reports', description: 'Comprehensive reporting system' },
  { id: 'dashboards', name: 'Dashboards', description: 'Customizable dashboards' },
  { id: 'secure_data_storage', name: 'Secure Data Storage', description: 'Enterprise-grade data security' },
  { id: 'hierarchy_reporting', name: 'Hierarchy and Reporting', description: 'Organizational hierarchy management' },
  { id: 'allocation_resource_management', name: 'Allocation and Resource Management', description: 'Resource allocation and management' },
  { id: 'targets_achievement', name: 'Targets and Achievement Tracking', description: 'Goal setting and tracking' },
  { id: 'currency_country_mapping', name: 'Currency and Country Mapping', description: 'Multi-currency and country support' },
  { id: 'timezone', name: 'Timezone', description: 'Multi-timezone support' },
  { id: 'email_notifications', name: 'Email Notifications', description: 'Automated email notifications' },
  { id: 'two_factor_mfa', name: '2-Factor MFA', description: 'Two-factor authentication security' },
  { id: 'enhanced_ui_ux', name: 'Enhanced UI & UX Dark/Light Mode', description: 'Modern UI with theme options' }
];

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    billingPeriod: 'all'
  });
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [createEditDrawerVisible, setCreateEditDrawerVisible] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form] = Form.useForm();
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'cards'
  const [showDetailView, setShowDetailView] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const watchedPlanType = Form.useWatch('planType', form);
  const watchedPricingType = Form.useWatch('pricingType', form);
  const isTrialPlan = watchedPlanType === 'trial';

  const STEP_FIELDS = [
    ['planType', 'name', 'description'], // step 0: basics
    isTrialPlan ? ['pricingType', 'price', 'trialDays'] : ['pricingType', 'price', 'billingPeriod'], // step 1: pricing
    [], // step 2: limits — none required; -1 means unlimited
    [], // step 3: features — no minimum count enforced (Free tier can have zero)
    [], // step 4: coupons — optional
  ];
  const TOTAL_STEPS = 5;

  // Fetch subscription plans
  const fetchPlans = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        ...filters,
        ...params
      };

      const response = await subscriptionPlansService.getPlans(queryParams);
      if (response.success) {
        setPlans(response.data.plans || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination?.totalRecords || 0
        }));
      }
    } catch (error) {
      message.error(error.message || 'Failed to fetch subscription plans');
    } finally {
      setLoading(false);
    }
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationConfig) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }));
  };

  // View plan details
  const viewPlan = (plan) => {
    setSelectedPlan(plan);
    setDetailModalVisible(true);
  };

  // Handle row/card click to show detail view
  const handlePlanClick = (planId) => {
    setSelectedPlanId(planId);
    setShowDetailView(true);
  };

  // Handle back from detail view
  const handleBackFromDetail = () => {
    setShowDetailView(false);
    setSelectedPlanId(null);
  };

  const MANDATORY_FEATURE_IDS = AVAILABLE_FEATURES.filter((f) => f.mandatory).map((f) => f.id);

  // Create new plan
  const createPlan = () => {
    setEditingPlan(null);
    form.resetFields();
    setSelectedFeatures([...MANDATORY_FEATURE_IDS]);
    setCurrentStep(0);
    form.setFieldsValue({
      planType: 'basic',
      isActive: true,
      isPublic: true,
      isPopular: false,
      sortOrder: 0,
      pricingType: 'per_user',
      billingPeriod: 'monthly',
      trialDays: 14,
      features: [],
      limits: {
        maxUsers: -1,
        maxLeads: -1,
        maxDeals: -1,
        maxStorage: -1,
        maxIntegrations: -1
      },
      metadata: {
        color: '#1890ff'
      }
    });
    setCreateEditDrawerVisible(true);
  };

  // Edit plan
  const editPlan = (plan) => {
    setEditingPlan(plan);
    setCurrentStep(0);

    // Extract feature IDs from plan features. Always merge in the mandatory
    // ones so legacy plans saved before the mandatory split still satisfy
    // the core requirement.
    const featureIds = (plan.features || []).map(f => f.id || f.name?.toLowerCase().replace(/\s+/g, '_').replace(/[&\/]/g, ''));
    const merged = Array.from(new Set([...MANDATORY_FEATURE_IDS, ...featureIds]));
    setSelectedFeatures(merged);
    
    form.setFieldsValue({
      ...plan,
      features: plan.features || [],
      limits: {
        maxUsers: plan.limits?.maxUsers || -1,
        maxLeads: plan.limits?.maxLeads || -1,
        maxDeals: plan.limits?.maxDeals || -1,
        maxStorage: plan.limits?.maxStorage || -1,
        maxIntegrations: plan.limits?.maxIntegrations || -1
      },
      metadata: {
        color: plan.metadata?.color || '#1890ff',
        icon: plan.metadata?.icon || '',
        badge: plan.metadata?.badge || ''
      }
    });
    setCreateEditDrawerVisible(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate every step before final submit so the user can't skip
      // required fields by jumping directly to step 4.
      const values = await form.validateFields();
      setSubmitting(true);

      const features = selectedFeatures.map(featureId => {
        const feature = AVAILABLE_FEATURES.find(f => f.id === featureId);
        return {
          id: feature.id,
          name: feature.name,
          description: feature.description,
          enabled: true,
        };
      });

      const planData = {
        ...values,
        features,
        limits: values.limits || {},
        metadata: values.metadata || {},
      };

      // For non-trial plans the trialDays field is irrelevant — strip it so
      // the API doesn't get confused.
      if (planData.planType !== 'trial') {
        delete planData.trialDays;
      }

      if (editingPlan) {
        await subscriptionPlansService.updatePlan(editingPlan._id, planData);
        message.success('Subscription plan updated successfully');
      } else {
        await subscriptionPlansService.createPlan(planData);
        message.success('Subscription plan created successfully');
      }

      setCreateEditDrawerVisible(false);
      fetchPlans();
    } catch (error) {
      if (!error?.errorFields) {
        message.error(error.message || 'Failed to save subscription plan');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Validate the current step's required fields, then advance.
  const goToNextStep = async () => {
    const fields = STEP_FIELDS[currentStep] || [];
    if (fields.length > 0) {
      try {
        await form.validateFields(fields);
      } catch (err) {
        // Validation error — stay on the current step.
        return;
      }
    }
    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS - 1));
  };

  const goToPreviousStep = () => setCurrentStep((s) => Math.max(s - 1, 0));

  // Toggle plan status
  const toggleStatus = async (planId) => {
    try {
      const response = await subscriptionPlansService.togglePlanStatus(planId);
      if (response.success) {
        message.success(response.message);
        fetchPlans();
      }
    } catch (error) {
      message.error(error.message || 'Failed to update plan status');
    }
  };

  // Delete plan
  const deletePlan = async (planId, planName) => {
    confirm({
      title: 'Delete Subscription Plan',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete the plan "${planName}"? This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await subscriptionPlansService.deletePlan(planId);
          message.success('Subscription plan deleted successfully');
          fetchPlans();
        } catch (error) {
          message.error(error.message || 'Failed to delete subscription plan');
        }
      }
    });
  };

  useEffect(() => {
    fetchPlans();
  }, [pagination.current, pagination.pageSize, filters]);

  // Calculate statistics
  const stats = {
    totalPlans: plans.length,
    activePlans: plans.filter(plan => plan.isActive).length,
    inactivePlans: plans.filter(plan => !plan.isActive).length,
    averagePrice: plans.length > 0 ? (plans.reduce((sum, plan) => sum + plan.price, 0) / plans.length).toFixed(2) : 0
  };

  // Table columns
  const columns = [
    {
      title: 'Plan',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            style={{ backgroundColor: record.metadata?.color || '#1890ff' }}
            icon={record.metadata?.icon ? <span>{record.metadata.icon}</span> : <CrownOutlined />}
          />
          <div>
            <div style={{ fontWeight: 'bold' }}>
              {text}
              {record.isPopular && (
                <Tag color="gold" style={{ marginLeft: 8 }}>
                  <StarOutlined /> Popular
                </Tag>
              )}
            </div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description?.substring(0, 50)}...
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price, record) => (
        <div>
          <Text strong style={{ fontSize: '16px' }}>
            ₹{Number(price || 0).toLocaleString('en-IN')}
          </Text>
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.pricingType === 'per_user' ? 'per user / ' : ''}per {record.billingPeriod}
            </Text>
          </div>
        </div>
      )
    },
    {
      title: 'Billing Period',
      dataIndex: 'billingPeriod',
      key: 'billingPeriod',
      render: (period) => (
        <Tag color={
          period === 'monthly' ? 'blue' : 
          period === 'yearly' ? 'green' : 
          'purple'
        }>
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </Tag>
      )
    },
    {
      title: 'Limits',
      key: 'limits',
      render: (_, record) => (
        <div>
          <div><UserOutlined /> Users: {record.limits?.maxUsers === -1 ? 'Unlimited' : record.limits?.maxUsers || 0}</div>
          <div><DatabaseOutlined /> Storage: {record.limits?.maxStorage === -1 ? 'Unlimited' : `${record.limits?.maxStorage || 0}GB`}</div>
        </div>
      )
    },
    {
      title: 'Features',
      dataIndex: 'features',
      key: 'features',
      render: (features) => (
        <div>
          {features?.slice(0, 2).map((feature, index) => (
            <Tag key={index} style={{ marginBottom: 2 }}>
              {feature.name}
            </Tag>
          ))}
          {features?.length > 2 && (
            <Tag>+{features.length - 2} more</Tag>
          )}
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'status',
      render: (isActive, record) => (
        <div>
          <Badge
            status={isActive ? 'success' : 'error'}
            text={isActive ? 'Active' : 'Inactive'}
          />
          <div style={{ marginTop: 4 }}>
            <Tag color={record.isPublic ? 'cyan' : 'default'} style={{ fontSize: '11px' }}>
              {record.isPublic ? '🌐 Public' : '🔒 Private'}
            </Tag>
          </div>
        </div>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewPlan(record)}
            />
          </Tooltip>
          <Tooltip title="Edit Plan">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => editPlan(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? 'Deactivate' : 'Activate'}>
            <Button
              type="text"
              icon={record.isActive ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => toggleStatus(record._id)}
            />
          </Tooltip>
          <Tooltip title="Delete Plan">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => deletePlan(record._id, record.name)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Render card view
  const renderCardView = () => (
    <Row gutter={[16, 16]}>
      {plans.map((plan) => (
        <Col xs={24} sm={12} lg={8} xl={6} key={plan._id}>
          <Card
            hoverable
            onClick={() => handlePlanClick(plan._id)}
            style={{ 
              height: '100%',
              cursor: 'pointer',
              borderColor: plan.isPopular ? '#faad14' : undefined,
              borderWidth: plan.isPopular ? 2 : 1
            }}
            cover={
              <div style={{ 
                padding: '24px', 
                textAlign: 'center', 
                background: `linear-gradient(135deg, ${plan.metadata?.color || '#1890ff'}, ${plan.metadata?.color || '#1890ff'}20)`
              }}>
                <Avatar 
                  size={64}
                  style={{ backgroundColor: plan.metadata?.color || '#1890ff' }}
                  icon={plan.metadata?.icon ? <span style={{ fontSize: '24px' }}>{plan.metadata.icon}</span> : <CrownOutlined />}
                />
                <div style={{ marginTop: 16 }}>
                  <Text style={{ fontSize: '32px', fontWeight: 'bold', color: '#fff' }}>
                    ₹{Number(plan.price || 0).toLocaleString('en-IN')}
                  </Text>
                  <div>
                    <Text style={{ color: '#fff', opacity: 0.8 }}>
                      per {plan.billingPeriod}
                    </Text>
                  </div>
                </div>
              </div>
            }
            actions={[
              <Tooltip title="View Details">
                <EyeOutlined onClick={(e) => { e.stopPropagation(); viewPlan(plan); }} />
              </Tooltip>,
              <Tooltip title="Edit Plan">
                <EditOutlined onClick={(e) => { e.stopPropagation(); editPlan(plan); }} />
              </Tooltip>,
              <Tooltip title={plan.isActive ? 'Deactivate' : 'Activate'}>
                {plan.isActive ? 
                  <CloseCircleOutlined onClick={(e) => { e.stopPropagation(); toggleStatus(plan._id); }} /> :
                  <CheckCircleOutlined onClick={(e) => { e.stopPropagation(); toggleStatus(plan._id); }} />
                }
              </Tooltip>
            ]}
          >
            <Card.Meta
              title={
                <Space>
                  {plan.name}
                  {plan.isPopular && (
                    <Tag color="gold" size="small">
                      <StarOutlined /> Popular
                    </Tag>
                  )}
                </Space>
              }
              description={
                <div>
                  <Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 12 }}>
                    {plan.description}
                  </Paragraph>
                  <div style={{ marginBottom: 8 }}>
                    <Tag color={
                      plan.billingPeriod === 'monthly' ? 'blue' : 
                      plan.billingPeriod === 'yearly' ? 'green' : 
                      'purple'
                    }>
                      <CalendarOutlined /> {plan.billingPeriod}
                    </Tag>
                    <Badge
                      status={plan.isActive ? 'success' : 'error'}
                      text={plan.isActive ? 'Active' : 'Inactive'}
                    />
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <div><UserOutlined /> Users: {plan.limits?.maxUsers === -1 ? '∞' : plan.limits?.maxUsers || 0}</div>
                    <div><DatabaseOutlined /> Storage: {plan.limits?.maxStorage === -1 ? '∞' : `${plan.limits?.maxStorage || 0}GB`}</div>
                    <div>{plan.features?.length || 0} features included</div>
                  </div>
                </div>
              }
            />
          </Card>
        </Col>
      ))}
    </Row>
  );

  // Show detail view if selected
  if (showDetailView && selectedPlanId) {
    return (
      <SubscriptionPlanDetail 
        planId={selectedPlanId}
        onBack={handleBackFromDetail}
      />
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          Subscription Plans Management
        </Title>
        <Text type="secondary">
          Create and manage subscription plans for organizations
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Plans"
              value={stats.totalPlans}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Plans"
              value={stats.activePlans}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Inactive Plans"
              value={stats.inactivePlans}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Average Price"
              value={stats.averagePrice}
              prefix="₹"
              formatter={(v) => Number(v || 0).toLocaleString('en-IN')}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Actions */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={8} md={6}>
            <Input
              placeholder="Search plans..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Status"
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              style={{ width: '100%' }}
              placeholder="Billing Period"
              value={filters.billingPeriod}
              onChange={(value) => setFilters(prev => ({ ...prev, billingPeriod: value }))}
            >
              <Option value="all">All Periods</Option>
              <Option value="monthly">Monthly</Option>
              <Option value="yearly">Yearly</Option>
              <Option value="lifetime">Lifetime</Option>
            </Select>
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Space>
              <Radio.Group 
                value={viewMode} 
                onChange={(e) => setViewMode(e.target.value)}
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="list">
                  <UnorderedListOutlined /> List
                </Radio.Button>
                <Radio.Button value="cards">
                  <AppstoreOutlined /> Cards
                </Radio.Button>
              </Radio.Group>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={createPlan}
              >
                Create Plan
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Plans Display */}
      <Card>
        {viewMode === 'list' ? (
          <Table
            columns={columns}
            dataSource={plans}
            rowKey="_id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} plans`
            }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
            onRow={(record) => ({
              onClick: () => handlePlanClick(record._id),
              style: { cursor: 'pointer' }
            })}
          />
        ) : (
          <div>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : (
              renderCardView()
            )}
          </div>
        )}
      </Card>

      {/* Plan Detail Modal */}
      <Modal
        title="Plan Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedPlan && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Plan Name" span={2}>
                <Space>
                  {selectedPlan.name}
                  {selectedPlan.isPopular && (
                    <Tag color="gold"><StarOutlined /> Popular</Tag>
                  )}
                  <Badge
                    status={selectedPlan.isActive ? 'success' : 'error'}
                    text={selectedPlan.isActive ? 'Active' : 'Inactive'}
                  />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {selectedPlan.description}
              </Descriptions.Item>
              <Descriptions.Item label="Price">
                ₹{Number(selectedPlan.price || 0).toLocaleString('en-IN')}
              </Descriptions.Item>
              <Descriptions.Item label="Billing Period">
                {selectedPlan.billingPeriod}
              </Descriptions.Item>
              <Descriptions.Item label="Max Users">
                {selectedPlan.limits?.maxUsers === -1 ? 'Unlimited' : selectedPlan.limits?.maxUsers || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Max Storage">
                {selectedPlan.limits?.maxStorage === -1 ? 'Unlimited' : `${selectedPlan.limits?.maxStorage || 0}GB`}
              </Descriptions.Item>
              <Descriptions.Item label="Max Leads">
                {selectedPlan.limits?.maxLeads === -1 ? 'Unlimited' : selectedPlan.limits?.maxLeads || 0}
              </Descriptions.Item>
              <Descriptions.Item label="Max Deals">
                {selectedPlan.limits?.maxDeals === -1 ? 'Unlimited' : selectedPlan.limits?.maxDeals || 0}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Features</Divider>
            <List
              dataSource={selectedPlan.features || []}
              renderItem={(feature) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                    title={feature.name}
                    description={feature.description}
                  />
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>

      {/* Create/Edit Plan Drawer with Stepper */}
      <Drawer
        title={editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
        open={createEditDrawerVisible}
        onClose={() => {
          setCreateEditDrawerVisible(false);
        }}
        width={720}
        destroyOnClose
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button onClick={() => setCreateEditDrawerVisible(false)}>Cancel</Button>
            <Space>
              {currentStep > 0 && <Button onClick={goToPreviousStep}>Previous</Button>}
              {currentStep < 4 ? (
                <Button type="primary" onClick={goToNextStep}>Next</Button>
              ) : (
                <Button type="primary" loading={submitting} onClick={handleSubmit}>
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              )}
            </Space>
          </div>
        }
      >
        <Steps
          current={currentStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Basics' },
            { title: 'Pricing' },
            { title: 'Limits' },
            { title: 'Features' },
            { title: 'Coupons' },
          ]}
        />
        <Form form={form} layout="vertical">
          {/* STEP 0 — Basics */}
          <div style={{ display: currentStep === 0 ? 'block' : 'none' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="planType"
                  label="Plan Type"
                  rules={[{ required: true, message: 'Please select plan type' }]}
                  extra="Maps to the tenant.plan field"
                >
                  <Select placeholder="Select plan type">
                    <Option value="free">Free</Option>
                    <Option value="trial">Trial</Option>
                    <Option value="basic">Basic</Option>
                    <Option value="premium">Premium</Option>
                    <Option value="professional">Professional</Option>
                    <Option value="enterprise">Enterprise</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Display Name"
                  rules={[{ required: true, message: 'Please enter display name' }]}
                  extra={'e.g. "Starter Pack", "Pro Bundle"'}
                >
                  <Input placeholder="e.g., Starter Pack, Pro Bundle" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea rows={3} placeholder="Describe this subscription plan…" />
            </Form.Item>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="isActive" label="Status" valuePropName="checked">
                  <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="isPublic"
                  label="Public Listing"
                  valuePropName="checked"
                  tooltip="When enabled, this plan is visible during signup"
                >
                  <Switch checkedChildren="Public" unCheckedChildren="Private" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="isPopular" label="Popular" valuePropName="checked">
                  <Switch checkedChildren="Yes" unCheckedChildren="No" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* STEP 1 — Pricing */}
          <div style={{ display: currentStep === 1 ? 'block' : 'none' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="pricingType"
                  label="Pricing Type"
                  rules={[{ required: true, message: 'Please select pricing type' }]}
                >
                  <Select>
                    <Option value="per_user">Per User</Option>
                    <Option value="flat">Flat Rate</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="billingPeriod"
                  label="Default Billing Period"
                  rules={[{ required: true, message: 'Please select billing period' }]}
                  tooltip="If you also set Annual Discount, both monthly + annual will be offered to buyers."
                >
                  <Select>
                    <Option value="monthly">Monthly</Option>
                    <Option value="yearly">Yearly</Option>
                    <Option value="lifetime">Lifetime</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="price"
                  label={`Price (₹) ${watchedPricingType === 'per_user' ? '— per user / month' : '— flat'}`}
                  rules={[{ required: true, message: 'Please enter price' }]}
                  extra={
                    watchedPricingType === 'per_user'
                      ? 'Total = price × user count, charged per cycle.'
                      : 'Flat rate regardless of number of users.'
                  }
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    addonBefore="₹"
                    addonAfter={watchedPricingType === 'per_user' ? 'per user' : 'flat'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="annualDiscountPercent"
                  label="Annual Discount (%)"
                  tooltip="If > 0, buyers can choose yearly billing and pay (price × 12 × (1 − discount%)). Set 0 for monthly-only."
                >
                  <InputNumber style={{ width: '100%' }} min={0} max={100} addonAfter="%" />
                </Form.Item>
              </Col>
            </Row>
            {isTrialPlan && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Trial plan"
                description="Trial plans don't charge — set the duration instead. Tenants on this plan get unlimited access for the configured number of days."
              />
            )}
            {isTrialPlan && (
              <Form.Item
                name="trialDays"
                label="Trial duration (days)"
                rules={[
                  { required: true, message: 'Enter trial duration in days' },
                  { type: 'number', min: 1, max: 365 },
                ]}
              >
                <InputNumber style={{ width: '100%' }} min={1} max={365} addonAfter="days" />
              </Form.Item>
            )}
          </div>

          {/* STEP 2 — Limits */}
          <div style={{ display: currentStep === 2 ? 'block' : 'none' }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Use -1 for unlimited."
            />
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name={['limits', 'maxUsers']} label="Max Users">
                  <InputNumber style={{ width: '100%' }} min={-1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['limits', 'maxStorage']} label="Max Storage (GB)">
                  <InputNumber style={{ width: '100%' }} min={-1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['limits', 'maxLeads']} label="Max Leads">
                  <InputNumber style={{ width: '100%' }} min={-1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['limits', 'maxDeals']} label="Max Deals">
                  <InputNumber style={{ width: '100%' }} min={-1} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={['limits', 'maxIntegrations']} label="Max Integrations">
                  <InputNumber style={{ width: '100%' }} min={-1} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* STEP 3 — Features */}
          <div style={{ display: currentStep === 3 ? 'block' : 'none' }}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              Tick the features unlocked by this plan. Tenants on this plan will only see / be able to use the features ticked here.
              <Tag color="purple" style={{ marginLeft: 8 }}>Required</Tag> features are core CRM modules and can't be removed.
            </Text>
            <Row gutter={[12, 12]}>
              {AVAILABLE_FEATURES.map((feature) => {
                const checked = selectedFeatures.includes(feature.id);
                const locked = Boolean(feature.mandatory);
                return (
                  <Col span={12} key={feature.id}>
                    <Card
                      size="small"
                      hoverable={!locked}
                      onClick={() => {
                        if (locked) return;
                        setSelectedFeatures((prev) =>
                          prev.includes(feature.id)
                            ? prev.filter((id) => id !== feature.id)
                            : [...prev, feature.id]
                        );
                      }}
                      style={{
                        cursor: locked ? 'not-allowed' : 'pointer',
                        border: checked ? '2px solid #6366f1' : '1px solid #e5e7eb',
                        backgroundColor: locked
                          ? '#f5f3ff'
                          : checked
                          ? '#eef2ff'
                          : '#fff',
                        opacity: locked ? 0.95 : 1,
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={locked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          if (locked) return;
                          setSelectedFeatures((prev) =>
                            e.target.checked
                              ? [...new Set([...prev, feature.id])]
                              : prev.filter((id) => id !== feature.id)
                          );
                        }}
                        style={{ width: '100%' }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Text strong>{feature.name}</Text>
                            {locked && <Tag color="purple" style={{ fontSize: 10 }}>Required</Tag>}
                          </div>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {feature.description}
                            </Text>
                          </div>
                        </div>
                      </Checkbox>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            <div style={{ marginTop: 12 }}>
              <Text type="secondary">
                Selected: {selectedFeatures.length} / {AVAILABLE_FEATURES.length} features
                {' · '}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {MANDATORY_FEATURE_IDS.length} required, {selectedFeatures.length - MANDATORY_FEATURE_IDS.length} optional
                </Text>
              </Text>
            </div>
          </div>

          {/* STEP 4 — Coupons */}
          <div style={{ display: currentStep === 4 ? 'block' : 'none' }}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Plan-specific coupon codes"
              description="Buyers can apply one of these codes at checkout to reduce the price. Leave 'Valid until' blank for no expiry, '0' for unlimited redemptions."
            />
            <Form.List name="coupons">
              {(fields, { add, remove }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {fields.map(({ key, name, ...rest }) => (
                    <Card key={key} size="small" style={{ background: '#f9fafb' }}>
                      <Row gutter={8} align="middle">
                        <Col span={6}>
                          <Form.Item
                            {...rest}
                            name={[name, 'code']}
                            label="Code"
                            rules={[{ required: true, message: 'Required' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Input placeholder="LAUNCH50" style={{ textTransform: 'uppercase' }} />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...rest}
                            name={[name, 'discountPercent']}
                            label="Discount %"
                            rules={[{ required: true, message: 'Required' }]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber min={0} max={100} addonAfter="%" style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...rest}
                            name={[name, 'maxRedemptions']}
                            label="Max uses"
                            tooltip="0 = unlimited"
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber min={0} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item
                            {...rest}
                            name={[name, 'validUntil']}
                            label="Valid until"
                            style={{ marginBottom: 0 }}
                          >
                            <Input type="date" />
                          </Form.Item>
                        </Col>
                        <Col span={3} style={{ textAlign: 'right' }}>
                          <Form.Item label=" " style={{ marginBottom: 0 }}>
                            <Button danger size="small" onClick={() => remove(name)}>
                              Remove
                            </Button>
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => add({ discountPercent: 10, maxRedemptions: 0, isActive: true })}
                    block
                  >
                    + Add coupon
                  </Button>
                </div>
              )}
            </Form.List>
          </div>
        </Form>
      </Drawer>
    </div>
  );
};

export default SubscriptionPlans;
